<template>
  <v-container fluid>
    <v-row>
      <v-col><h2>{{ $t('admin_acc_public_holiday.title') }}</h2></v-col>
      <v-col class="text-right">
        <v-menu v-model="addMenuOpened"
                offset-y
                :close-on-content-click="false">
          <template v-slot:activator="{on, attrs}">
            <v-btn v-on="on">{{ $t('admin_acc_public_holiday.add_title') }}</v-btn>
          </template>

          <v-card outlined style="border-width: 2px; border-color: #333;">
            <v-card-title>{{ $t('admin_acc_public_holiday.add_title') }}</v-card-title>
            <v-card-text>
              <v-row>
                <v-col>
                  <v-text-field v-model="addingHolidayName"
                                :label="$t('admin_acc_public_holiday.name')"
                                :placeholder="$t('admin_acc_public_holiday.name')"></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <DateInput v-model="addingHolidayDate"
                             :label="$t('admin_acc_public_holiday.date')"
                             :placeholder="$t('admin_acc_public_holiday.date')"></DateInput>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text color="red"
                     @click="addMenuOpened = false">
                {{ $t('cancel') }}
              </v-btn>
              <v-btn text color="#2bc7ca"
                     @click="addHoliday">
                {{ $t('save') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-menu>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-menu ref="datePickerMenu"
                v-model="fromMonthDatePickerMenuOpened"
                :close-on-content-click="false"
                transition="scale-transition"
                bottom>
          <template v-slot:activator="{on, attrs}">
            <v-text-field outlined
                          v-model="fromMonth"
                          append-icon="mdi-calendar"
                          readonly
                          v-on="on"></v-text-field>
          </template>
          <v-date-picker type="month"
                         no-title
                         scrollable
                         v-model="fromMonth"
                         @input="fromMonthDatePickerMenuOpened = false">
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col>
        <v-menu ref="datePickerMenu"
                v-model="toMonthDatePickerMenuOpened"
                :close-on-content-click="false"
                transition="scale-transition"
                bottom>
          <template v-slot:activator="{on, attrs}">
            <v-text-field outlined
                          v-model="toMonth"
                          append-icon="mdi-calendar"
                          readonly
                          v-on="on"></v-text-field>
          </template>
          <v-date-picker type="month"
                         no-title
                         scrollable
                         v-model="toMonth"
                         @input="toMonthDatePickerMenuOpened = false">
          </v-date-picker>
        </v-menu>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-data-table :items="holidays"
                      :headers="holidayHeaders"
                      :loading="holidays == null">
          <template v-slot:item.actions="data">
            <font-awesome-icon icon="edit"
                               class="mr-3"
                               @click.stop="editHoliday(data.item)"></font-awesome-icon>
            <font-awesome-icon icon="trash"
                               @click.stop="deleteHoliday(data.item)"></font-awesome-icon>
          </template>
        </v-data-table>
      </v-col>
      <v-col v-if="editingHoliday">
        <v-card>
          <v-card-title>{{$t('admin_acc_public_holiday.edit_title')}}</v-card-title>
          <v-card-text>
            <v-row>
              <v-col>
                <v-text-field v-model="editingHoliday.name"
                              :label="$t('admin_acc_public_holiday.name')"
                              :placeholder="$t('admin_acc_public_holiday.name')"></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <DateInput v-model="editingHoliday.date"
                           :label="$t('admin_acc_public_holiday.date')"
                           :placeholder="$t('admin_acc_public_holiday.date')"></DateInput>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text color="red"
                   @click="editingHoliday = null">
              {{ $t('cancel') }}
            </v-btn>
            <v-btn text color="#2bc7ca"
                   @click="saveHoliday">
              {{ $t('save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {onMounted, ref, watch} from '@vue/composition-api'
import {DateTime} from 'luxon'
import DateInput from "@/components/DateInput";
import _ from 'lodash';

export default {
  name: 'AccPublicHolidayList',
  components: {DateInput},
  setup(props, {root}) {
    const addMenuOpened = ref(false);
    const addingHolidayName = ref('');
    const addingHolidayDate = ref(null);

    const addHoliday = function () {
      root.$callAPI('room/admin/public_holiday/set/', {
        name: addingHolidayName.value,
        date: DateTime.fromISO(addingHolidayDate.value).toISODate(),
      }, function (response) {
        addMenuOpened.value = false;
        addingHolidayName.value = '';
        addingHolidayDate.value = null;
        getHolidays();
      });
    };

    const fromMonth = ref(DateTime.fromJSDate(new Date).startOf('year').toFormat('yyyy-MM'));
    const fromMonthDatePickerMenuOpened = ref(false);
    const toMonth = ref(DateTime.fromJSDate(new Date).endOf('year').toFormat('yyyy-MM'));
    const toMonthDatePickerMenuOpened = ref(false);

    const holidays = ref([]);
    const holidayHeaders = ref([
      {text: root.$t('admin_acc_public_holiday.name'), value: 'name'},
      {text: root.$t('admin_acc_public_holiday.date'), value: 'date'},
      {text: '', value: 'actions', align: 'end'},
    ]);
    const getHolidays = function () {
      root.$callAPI('room/admin/public_holiday/', {
        from_date: DateTime.fromFormat(fromMonth.value, 'yyyy-MM').startOf('month').toISODate(),
        to_date: DateTime.fromFormat(toMonth.value, 'yyyy-MM').endOf('month').toISODate(),
      }, function (response) {
        holidays.value = response.body.holidays;
      });
    };

    const editingHoliday = ref(null);
    const editHoliday = function (item) {
      editingHoliday.value = _.cloneDeep(item);
    };
    const saveHoliday = function () {
      root.$callAPI('room/admin/public_holiday/set/', {
        id: editingHoliday.value.id,
        name: editingHoliday.value.name,
        date: DateTime.fromISO(editingHoliday.value.date).toISODate(),
      }, function (response) {
        editingHoliday.value = null;
        getHolidays();
      });
    };

    const deleteHoliday = function (item) {
      if (confirm(root.$t('admin_acc_public_holiday.confirm_delete'))) {
        root.$callAPI('room/admin/public_holiday/delete/', {id: item.id}, function (response) {
          getHolidays();
        })
      }
    };

    watch([fromMonth, toMonth], () => {
      getHolidays();
    });

    onMounted(() => {
      getHolidays();
    })

    return {
      addMenuOpened,
      addingHolidayName,
      addingHolidayDate,
      addHoliday,

      fromMonth,
      fromMonthDatePickerMenuOpened,
      toMonth,
      toMonthDatePickerMenuOpened,

      holidays,
      holidayHeaders,

      editingHoliday,
      editHoliday,
      saveHoliday,

      deleteHoliday,
    };
  },
}
</script>